/**
 * Error Display Component
 * Displays error messages with minimalist design.
 */

import styles from './ErrorDisplay.module.css';
import { useTranslation } from '../hooks/useTranslation.tsx';

/**
 * Props for Error Display Component
 */
export interface ErrorDisplayProps {
  /** Error message to display */
  message: string;
  /** Optional retry callback */
  onRetry?: () => void;
  /** Whether error is dismissible */
  dismissible?: boolean;
  /** Callback when error is dismissed */
  onDismiss?: () => void;
}

/**
 * Error Display Component
 * Shows error messages with retry option.
 */
export const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  message,
  onRetry,
  dismissible = false,
  onDismiss,
}) => {
  const { t } = useTranslation();
  return (
    <div className={styles.error} role="alert">
      <div className={styles.errorContent}>
        <span className={styles.errorIcon}>⚠</span>
        <span className={styles.errorMessage}>{message}</span>
      </div>
      <div className={styles.errorActions}>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className={styles.retryButton}
            aria-label={t.error.retry}
          >
            {t.error.retry}
          </button>
        )}
        {dismissible && onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className={styles.dismissButton}
            aria-label={t.error.dismiss}
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
};

